import DownloadIcon from "@mui/icons-material/Download";
import {
    Alert,
    Box,
    Button,
    FormControl,
    FormHelperText,
    Grid,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material";

import { faEdit, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonVariant } from "Constant/button";
import { capitalizeFirstLetter } from "Constant/string";
import Form from "react-bootstrap/Form";
import { Controller } from "react-hook-form";

import StandartButton from "components/atoms/StandartButton";
import TextArea from "components/atoms/TextArea";
import HtmlCodeEditor from "components/molecules/HtmlCodeEditor";
import ImageUploadInput from "components/molecules/ImageUploadInput";
import VideoUploadInput from "components/molecules/VideUploadInput";
import MouseOverPopover from "components/MouseOverPopover";

export function validateHtmlSnippet(input: string) {
    const errors: string[] = [];

    // 1. Malformed tag check
    const malformed = input.match(/<[^>]*$/);
    if (malformed) {
        errors.push(
            `Invalid HTML structure: "${malformed[0]}" is an incomplete or malformed tag.`,
        );
    }

    // 2. Forbidden tags
    const forbiddenTags = ["html", "body"];
    forbiddenTags.forEach((tag) => {
        const regex = new RegExp(`<\\/?${tag}\\b[^>]*>`, "gi");
        if (regex.test(input)) {
            errors.push(`Invalid tag detected: <${tag}> tag is not allowed.`);
        }
    });

    const voidElements = [
        "area",
        "base",
        "br",
        "col",
        "embed",
        "hr",
        "img",
        "input",
        "link",
        "meta",
        "param",
        "source",
        "track",
        "wbr",
    ];

    // 4. Validate <style> content
    const styleRegex = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
    let match;
    while ((match = styleRegex.exec(input)) !== null) {
        const cssContent = match[1].trim();

        // Basic CSS block match: selector { property: value; }
        const cssBlocks = cssContent.match(/[^{}]+\{[^}]+\}/g);
        const openBraces = (cssContent.match(/{/g) || []).length;
        const closeBraces = (cssContent.match(/}/g) || []).length;

        if (openBraces !== closeBraces) {
            errors.push(`<style> block has mismatched curly braces.`);
        }

        if (!cssBlocks && cssContent !== "") {
            errors.push(`Invalid CSS inside <style> tag.`);
        }
    }

    const scannableInput = input
        .replace(/<!--[\s\S]*?-->/g, "")
        .replace(/<!\[CDATA\[[\s\S]*?\]\]>/g, "")
        .replace(/<(style|script)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, "");

    const tagRegex =
        /<(\/)?([a-zA-Z][a-zA-Z0-9:._-]*)((?:"[^"]*"|'[^']*'|[^"'>])*)>/g;

    const tagCounts: Record<string, number> = {};

    while ((match = tagRegex.exec(scannableInput)) !== null) {
        const isClosingTag = !!match[1];
        const tagName = match[2].toLowerCase();
        const isSelfClosing = /\/\s*$/.test(match[3]);

        if (isClosingTag) {
            tagCounts[tagName] = (tagCounts[tagName] || 0) - 1;
        } else if (!isSelfClosing && voidElements.indexOf(tagName) === -1) {
            tagCounts[tagName] = (tagCounts[tagName] || 0) + 1;
        }
    }

    for (const tag in tagCounts) {
        if (tagCounts[tag] !== 0) {
            errors.push(
                `Mismatched tag count: <${tag}> opening and closing tags do not match.`,
            );
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
}
/**
 * @description renderTemplateInput recursive template input renderer
 */
export const renderTemplateInput = ({
    errors,
    field,
    hookForm,
    gamificationItemsArrayForm,
    selectedAppTypes,
    dispatchGetAppDetails,
    appleIdField,
    mmpField,
    appDetails,
    htmlValidationMessage,
    marketplaceInstalled = false,
    isArray = false,
    oneLinkValidation,
    disabled = false,
}) => {
    const { control, register, setValue } = hookForm;

    // OneLink validation reports redirection errors per platform, but only the
    // selected store types matter: an unselected platform is neither reported
    // nor allowed to fail the field. Among the selected ones, a platform
    // without a message redirected correctly and is shown as a success line
    // beside any failing one.
    const checksOneLinkAndroid = !!selectedAppTypes?.includes("android");
    const checksOneLinkIos = !!selectedAppTypes?.includes("apple");
    const oneLinkData = oneLinkValidation?.result?.data;
    const oneLinkAndroidError = checksOneLinkAndroid
        ? oneLinkData?.android
        : undefined;
    const oneLinkIosError = checksOneLinkIos ? oneLinkData?.ios : undefined;
    // A result carrying no per-platform message is a global failure or a clean
    // pass, so the per-platform lines only make sense alongside one.
    const hasOneLinkPlatformData = !!(oneLinkData?.android || oneLinkData?.ios);
    const showOneLinkPlatformStatus =
        (checksOneLinkAndroid || checksOneLinkIos) &&
        (hasOneLinkPlatformData || oneLinkValidation?.result?.status === true);
    const {
        fields: gamificationFields,
        append: appendGamificationField,
        remove: removeGamificationField,
    } = gamificationItemsArrayForm || {};
    const handlers = [
        {
            predicate: () => field?.type === "button",
            handler: () => (
                <Box className="my-3" width={"100%"}>
                    <TextField
                        {...register(`${field?.name}.description`)}
                        label={field?.description}
                        style={{ width: "100%" }}
                        className="text-field-ui-workaround"
                        required={field?.required}
                    />
                </Box>
            ),
        },
        {
            predicate: () => field?.type === "input",
            handler: () => {
                const shouldRender =
                    field?.name !== "cpp_ios" &&
                    field?.name !== "cpp_android" &&
                    field?.name !== "target_link"
                        ? true
                        : field?.name === "cpp_ios" &&
                            selectedAppTypes?.includes("apple")
                          ? true
                          : field?.name === "cpp_android" &&
                              selectedAppTypes?.includes("android")
                            ? true
                            : field?.name === "target_link" &&
                              selectedAppTypes?.includes("web");
                const fieldError = errors?.[field?.name];
                return (
                    shouldRender && (
                        <FormControl
                            fullWidth
                            className="mouse-over-popover-area"
                            error={!!fieldError}
                        >
                            <TextField
                                label={field?.description}
                                {...register(field?.name)}
                                style={{ width: "100%" }}
                                className={`text-field-ui-workaround ${
                                    isArray ? "mt-3" : ""
                                }`}
                                placeholder={field?.placeholder}
                                fullWidth
                                error={!!fieldError}
                                helperText={fieldError?.message}
                            />
                            {field?.tooltip && (
                                <MouseOverPopover
                                    text={field?.tooltip}
                                    withAutocomplete
                                />
                            )}
                        </FormControl>
                    )
                );
            },
        },
        {
            predicate: () => field?.type === "multiline_input",
            handler: () => (
                <>
                    {field?.name === "html_description" && (
                        <Alert
                            variant="outlined"
                            severity="info"
                            className="mb-3"
                        >
                            Please enter valid HTML content.
                            <br />
                            Avoid including <code>&lt;html&gt;</code> or{" "}
                            <code>&lt;body&gt;</code> tags.
                        </Alert>
                    )}
                    {field?.name === "html_description" && (
                        <Alert
                            variant="outlined"
                            severity="primary"
                            className="mb-3"
                        >
                            When using the Custom HTML layout, please use the{" "}
                            <code
                                style={{
                                    backgroundColor: "#fff3e0",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontWeight: 600,
                                }}
                            >
                                redirectToAppStore()
                            </code>{" "}
                            function as the <code>onclick</code> handler for any
                            button or anchor (<code>&lt;a&gt;</code>) elements
                            you create.
                        </Alert>
                    )}
                    {field?.name === "html_description" ? (
                        <Controller
                            name={field.name}
                            control={control}
                            render={({
                                field: {
                                    value,
                                    onChange,
                                    onBlur,
                                },
                            }) => (
                                <HtmlCodeEditor
                                    value={value || ""}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    label={field?.description}
                                    placeholder={field?.description}
                                    required={field?.required}
                                    error={
                                        !!errors?.[field?.name] ||
                                        !htmlValidationMessage?.isValid
                                    }
                                />
                            )}
                        />
                    ) : (
                        <TextArea
                            minRows={5}
                            maxRows={300}
                            label={field?.description}
                            placeholder={field?.description}
                            {...register(field?.name)}
                            style={{ width: "100%" }}
                            required={field?.required}
                            error={!!errors?.[field?.name]}
                        />
                    )}
                    {errors?.[field?.name]?.message && (
                        <FormHelperText error>
                            {errors[field.name].message}
                        </FormHelperText>
                    )}
                    {field?.name === "html_description" &&
                        htmlValidationMessage?.errors?.map((message) => (
                            <FormHelperText error>{message}</FormHelperText>
                        ))}
                    {/* {field?.name === "html_description" && (
                        <>
                            <Box
                                marginTop={2}
                                marginBottom={2}
                                textAlign="center"
                            >
                                OR
                            </Box>
                            <StandartButton
                                variant={ButtonVariant.WHITE_SHADOWED}
                                style={{ width: "100%" }}
                                className="mt-2"
                                onClick={() =>
                                    window.open(
                                        `${window.location.origin}/custom-html-editor`,
                                    )
                                }
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="center"
                                    width="100%"
                                    gap={1}
                                >
                                    <OpenInNewIcon />
                                    <Box>Custom HTML Editor</Box>
                                </Stack>
                            </StandartButton>
                        </>
                    )} */}
                </>
            ),
        },
        {
            predicate: () => field?.type === "array",
            handler: () => {
                return (
                    <Box
                        style={{
                            background: "#F8F8F8",
                            fontWeight: 700,
                            padding: "1rem",
                            borderRadius: "8px",
                        }}
                    >
                        <Box>
                            <Box>{field?.description}</Box>
                            {gamificationFields?.map((item, i) => {
                                return (
                                    <Box
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: `${Array.from({
                                                length: field?.attributes
                                                    ?.length,
                                            })
                                                ?.fill("minMax(0, 1fr)")
                                                .join(" ")} 50px`,
                                            gap: "8px",
                                        }}
                                    >
                                        {field?.attributes?.map((attribute) => (
                                            <>
                                                {renderTemplateInput({
                                                    field: {
                                                        ...attribute,
                                                        name: `${field?.name}.${i}.${attribute?.name}`,
                                                    },
                                                    appDetails,
                                                    appleIdField,
                                                    dispatchGetAppDetails,
                                                    gamificationItemsArrayForm,
                                                    hookForm,
                                                    mmpField,
                                                    selectedAppTypes,
                                                    htmlValidationMessage,
                                                    errors,
                                                    isArray: true,
                                                    oneLinkValidation,
                                                })}
                                            </>
                                        ))}
                                        <Stack
                                            justifyContent="center"
                                            alignItems="center"
                                            direction="row"
                                            height="100%"
                                        >
                                            <Tooltip
                                                title={
                                                    gamificationFields?.length +
                                                        -1 <
                                                        field?.min_count &&
                                                    `Item rows must not be fewer than. ${field?.min_count}.`
                                                }
                                            >
                                                <Box>
                                                    <Button
                                                        onClick={() =>
                                                            removeGamificationField(
                                                                i,
                                                            )
                                                        }
                                                        color="error"
                                                        style={{
                                                            minWidth:
                                                                "unset !important",
                                                            width: "fit-content",
                                                            transform:
                                                                "translateY(30%)",
                                                        }}
                                                        disabled={
                                                            gamificationFields?.length +
                                                                -1 <
                                                            field?.min_count
                                                        }
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            gap={1}
                                                            justifyContent="center"
                                                            alignItems="center"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faTrashAlt
                                                                }
                                                                className="iconStyle"
                                                                fontSize={
                                                                    "16px"
                                                                }
                                                            />
                                                        </Stack>
                                                    </Button>
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                );
                            })}
                            <Grid
                                size={{
                                    xs: 12,
                                }}
                            >
                                <Tooltip
                                    title={
                                        gamificationFields?.length + 1 >
                                            field?.max_count &&
                                        `Item rows must not exceed ${field?.max_count}.`
                                    }
                                >
                                    <Box>
                                        <Button
                                            color="primary"
                                            variant="outlined"
                                            disabled={
                                                gamificationFields?.length + 1 >
                                                field?.max_count
                                            }
                                            className="mt-3"
                                            onClick={() =>
                                                appendGamificationField({})
                                            }
                                        >
                                            Add item
                                        </Button>
                                    </Box>
                                </Tooltip>
                            </Grid>
                        </Box>
                    </Box>
                );
            },
        },
        {
            predicate: () => field?.type === "image",
            handler: () => (
                <Controller
                    control={control}
                    name="logo"
                    render={({ fieldState: { error: fieldError } }) => (
                        <ImageUploadInput
                            control={control}
                            name="logo"
                            acceptedFileTypes={[
                                "image/jpg",
                                "image/jpeg",
                                "image/png",
                                "image/jpg",
                                "image/gif",
                            ]}
                            error={!!fieldError}
                            helperText={fieldError?.message}
                        />
                    )}
                />
            ),
        },
        {
            predicate: () => field?.type === "video",
            handler: () => (
                <VideoUploadInput
                    name={field?.name}
                    control={control}
                    label="Drop your video here or click to upload"
                    onDragLabel="Drop your video here"
                />
            ),
        },
        {
            predicate: () => field?.type === "combobox",
            handler: () => (
                <>
                    <Controller
                        control={control}
                        name={field?.name}
                        render={({
                            field: { onChange, value, name },
                            fieldState: { error },
                        }) => (
                            <FormControl
                                fullWidth
                                className="mouse-over-popover-area"
                                error={!!error}
                            >
                                <Select
                                    label={field?.description}
                                    name={name}
                                    value={value || []}
                                    onChange={onChange}
                                    error={!!error}
                                    multiple={field?.type === "multi_select"}
                                    fullWidth
                                    required={field?.required}
                                    disabled={disabled}
                                >
                                    {Array.isArray(field?.options) &&
                                        field?.options?.map((option) => (
                                            <MenuItem
                                                key={option}
                                                value={option}
                                            >
                                                {capitalizeFirstLetter(option)}
                                            </MenuItem>
                                        ))}
                                </Select>
                                {error && (
                                    <FormHelperText error>
                                        {error.message}
                                    </FormHelperText>
                                )}
                                {field?.tooltip && (
                                    <MouseOverPopover
                                        text={field?.tooltip}
                                        withAutocomplete
                                    />
                                )}
                            </FormControl>
                        )}
                    />

                    {field?.name === "mmp" &&
                        (mmpField === "adjust" ||
                            (mmpField === "appsflyer" &&
                                !marketplaceInstalled)) && (
                            <FormControl
                                fullWidth
                                className="mouse-over-popover-area"
                            >
                                <TextField
                                    label={"One link"}
                                    {...register("one_link")}
                                    style={{ width: "100%" }}
                                    className="text-field-ui-workaround"
                                />
                                <MouseOverPopover
                                    text={
                                        "OneLink is used to specify the page where the user will be redirected after a certain period. It is designed to optimize user experience and ensure accurate redirection."
                                    }
                                    withAutocomplete
                                />

                                {(oneLinkValidation?.isValidating ||
                                    oneLinkValidation?.result) && (
                                    <Stack sx={{ mt: 1 }} gap={1}>
                                        {/* Validation Loading State */}
                                        {oneLinkValidation?.isValidating && (
                                            <FormHelperText>
                                                Validating OneLink...
                                            </FormHelperText>
                                        )}

                                        {/* Platform Not Supported Error */}
                                        {oneLinkValidation?.result?.status ===
                                            false &&
                                            !hasOneLinkPlatformData && (
                                                <FormHelperText error>
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        gap={1}
                                                    >
                                                        <img
                                                            src="/assets/images/danger.svg"
                                                            alt="error"
                                                            style={{
                                                                width: "16px",
                                                                height: "16px",
                                                            }}
                                                        />
                                                        <span>
                                                            {
                                                                oneLinkValidation
                                                                    ?.result
                                                                    ?.message
                                                            }
                                                        </span>
                                                    </Stack>
                                                </FormHelperText>
                                            )}

                                        {/* Android Redirection Status */}
                                        {showOneLinkPlatformStatus &&
                                            checksOneLinkAndroid && (
                                                <FormHelperText
                                                    error={
                                                        !!oneLinkAndroidError
                                                    }
                                                    sx={
                                                        oneLinkAndroidError
                                                            ? undefined
                                                            : {
                                                                  color: "success.main",
                                                              }
                                                    }
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        gap={1}
                                                    >
                                                        <img
                                                            src={
                                                                oneLinkAndroidError
                                                                    ? "/assets/images/failed-android.svg"
                                                                    : "/assets/images/checked-android.svg"
                                                            }
                                                            alt={
                                                                oneLinkAndroidError
                                                                    ? "android error"
                                                                    : "android success"
                                                            }
                                                            style={{
                                                                width: "20px",
                                                                height: "20px",
                                                            }}
                                                        />
                                                        <span>
                                                            {oneLinkAndroidError ||
                                                                "Android redirection is valid"}
                                                        </span>
                                                    </Stack>
                                                </FormHelperText>
                                            )}

                                        {/* iOS Redirection Status */}
                                        {showOneLinkPlatformStatus &&
                                            checksOneLinkIos && (
                                                <FormHelperText
                                                    error={!!oneLinkIosError}
                                                    sx={
                                                        oneLinkIosError
                                                            ? undefined
                                                            : {
                                                                  color: "success.main",
                                                              }
                                                    }
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        gap={1}
                                                    >
                                                        <img
                                                            src={
                                                                oneLinkIosError
                                                                    ? "/assets/images/failed-ios.svg"
                                                                    : "/assets/images/checked-apple.svg"
                                                            }
                                                            alt={
                                                                oneLinkIosError
                                                                    ? "ios error"
                                                                    : "ios success"
                                                            }
                                                            style={{
                                                                width: "20px",
                                                                height: "20px",
                                                            }}
                                                        />
                                                        <span>
                                                            {oneLinkIosError ||
                                                                "iOS redirection is valid"}
                                                        </span>
                                                    </Stack>
                                                </FormHelperText>
                                            )}

                                        {/* Success State */}
                                        {oneLinkValidation?.result?.status ===
                                            true &&
                                            !showOneLinkPlatformStatus && (
                                                <FormHelperText
                                                    sx={{
                                                        color: "success.main",
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        gap={1}
                                                    >
                                                        <img
                                                            src="/assets/images/checked-android.svg"
                                                            alt="android success"
                                                            style={{
                                                                width: "20px",
                                                                height: "20px",
                                                            }}
                                                        />
                                                        <img
                                                            src="/assets/images/checked-apple.svg"
                                                            alt="ios success"
                                                            style={{
                                                                width: "20px",
                                                                height: "20px",
                                                            }}
                                                        />
                                                        <span>
                                                            OneLink is valid and
                                                            properly configured
                                                        </span>
                                                    </Stack>
                                                </FormHelperText>
                                            )}
                                    </Stack>
                                )}
                            </FormControl>
                        )}
                </>
            ),
        },
        {
            predicate: () => field?.type === "multi_select",
            handler: () => (
                <>
                    <Controller
                        control={control}
                        name={field?.name}
                        render={({
                            field: { onChange, value, name },
                            fieldState: { error },
                        }) => (
                            <FormControl
                                fullWidth
                                className="mouse-over-popover-area"
                            >
                                <Select
                                    name={name}
                                    value={value || []}
                                    label={field?.description}
                                    onChange={onChange}
                                    error={!!error}
                                    multiple
                                    fullWidth
                                    renderValue={(selected) =>
                                        selected
                                            ?.map((option) =>
                                                capitalizeFirstLetter(option),
                                            )
                                            ?.join(", ")
                                    }
                                    required={field?.required}
                                    disabled={disabled}
                                >
                                    {Array.isArray(field?.options) &&
                                        field?.options?.map((option) => {
                                            return (
                                                <MenuItem
                                                    key={option}
                                                    value={option}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        gap={1}
                                                        alignItems="center"
                                                    >
                                                        <Form.Check
                                                            type={"checkbox"}
                                                            checked={value?.includes(
                                                                option,
                                                            )}
                                                            style={{
                                                                marginLeft:
                                                                    "2rem",
                                                                height: "1.3rem",
                                                            }}
                                                        />
                                                        <ListItemText
                                                            primary={capitalizeFirstLetter(
                                                                option,
                                                            )}
                                                        />
                                                    </Stack>
                                                </MenuItem>
                                            );
                                        })}
                                </Select>
                                {field?.tooltip && (
                                    <MouseOverPopover
                                        text={field?.tooltip}
                                        withAutocomplete
                                    />
                                )}
                            </FormControl>
                        )}
                    />

                    {field?.name === "app_type" &&
                        selectedAppTypes?.includes("apple") && (
                            <Box
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "minMax(0,11fr) minMax(0, 45px)",
                                }}
                            >
                                <TextField
                                    label={"Apple App Store ID"}
                                    style={{ width: "100%" }}
                                    className=" text-field-ui-workaround"
                                    required={field?.required}
                                    {...register("applications.apple")}
                                    placeholder={"idxxxxxxxxxx"}
                                    error={errors?.applications?.apple}
                                />
                                <Tooltip title="Get details from apple and fill fields.">
                                    <Box
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            marginTop: "1rem",
                                        }}
                                    >
                                        <StandartButton
                                            onClick={() =>
                                                dispatchGetAppDetails(
                                                    appleIdField,
                                                )
                                            }
                                            iconButton
                                            variant={
                                                ButtonVariant.BLACK_OUTLINED
                                            }
                                            disabled={
                                                appDetails?.isLoading ||
                                                !appleIdField ||
                                                appleIdField?.trim() === ""
                                            }
                                        >
                                            <DownloadIcon />
                                        </StandartButton>
                                    </Box>
                                </Tooltip>
                            </Box>
                        )}
                    {field?.name === "app_type" &&
                        selectedAppTypes?.includes("apple") && (
                            <FormControl>
                                <TextField
                                    label={
                                        "IOS Cpp Url (Custom Product Page Url)"
                                    }
                                    {...register("cpp_ios")}
                                    style={{ width: "100%" }}
                                    className="text-field-ui-workaround"
                                />
                                <FormHelperText>
                                    Specify the URL of the custom product page
                                    for iOS. This URL is used to redirect users
                                    to a specific product page on the App Store
                                    and is ideal for personalizing user
                                    experiences.
                                </FormHelperText>
                            </FormControl>
                        )}
                    {field?.name === "app_type" &&
                        selectedAppTypes?.includes("android") && (
                            <TextField
                                label={"Android App Package Name"}
                                {...register("applications.android")}
                                style={{ width: "100%" }}
                                className="text-field-ui-workaround"
                                placeholder={"com.package.name"}
                                error={errors?.applications?.android}
                            />
                        )}
                    {field?.name === "app_type" &&
                        selectedAppTypes?.includes("android") && (
                            <FormControl>
                                <TextField
                                    label={
                                        "Android Cpp Url (Custom Product Page Url)"
                                    }
                                    {...register("cpp_android")}
                                    style={{ width: "100%" }}
                                    className="text-field-ui-workaround"
                                />
                                <FormHelperText>
                                    Specify the URL of the custom product page
                                    for Android. This URL defines the specific
                                    product page users will be redirected to and
                                    helps optimize the performance of your
                                    campaigns.
                                </FormHelperText>
                            </FormControl>
                        )}
                    {field?.name === "app_type" &&
                        selectedAppTypes?.includes("whatsapp") && (
                            <TextField
                                label={"Phone Number"}
                                {...register("applications.phone_number")}
                                style={{ width: "100%" }}
                                className="text-field-ui-workaround"
                                placeholder={"905555555555"}
                                error={errors?.applications?.phone_number}
                            />
                        )}
                </>
            ),
        },
        {
            predicate: () => field?.type === "step_builder",
            handler: () => {
                // Return null for step_builder as it will be handled separately in PageContentStep
                return null;
            },
        },
    ];
    return handlers.find(({ predicate }) => predicate())?.handler();
};

export const renderPaywallInputs = ({
    control,
    register,
    selectedProvider,
    isPaywallActivated,
    isAPIKeyInputDisabled,
    setIsAPIKeyInputDisabled,
    setValue,
}) => {
    return (
        <>
            <Controller
                control={control}
                name="paywall_integration.provider"
                render={({
                    field: { onChange, name, value },
                    fieldState: { error },
                }) => (
                    <FormControl fullWidth>
                        <Select
                            name={name}
                            value={value}
                            label="Provider"
                            onChange={onChange}
                            error={!!error}
                            fullWidth
                            className="mt-3"
                        >
                            {[{ label: "Paddle", value: "paddle" }]?.map(
                                (template) => (
                                    <MenuItem
                                        key={template?.value}
                                        value={template?.value}
                                    >
                                        {template?.label}
                                    </MenuItem>
                                ),
                            )}
                        </Select>
                    </FormControl>
                )}
            />
            {selectedProvider && (
                <>
                    <TextField
                        label={"Client Token"}
                        {...register("paywall_integration.client_token")}
                        style={{ width: "100%" }}
                        className="mt-3 text-field-ui-workaround"
                    />
                    <Stack direction="row" gap={2}>
                        <TextField
                            label={"API Key"}
                            {...register("paywall_integration.api_key")}
                            style={{ width: "100%" }}
                            className="mt-3 text-field-ui-workaround"
                            disabled={isAPIKeyInputDisabled}
                            {...(isAPIKeyInputDisabled
                                ? {
                                      value: "***********",
                                  }
                                : {})}
                        />
                        {isAPIKeyInputDisabled && (
                            <Tooltip title="Edit">
                                <Box
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        marginTop: "1rem",
                                    }}
                                >
                                    <StandartButton
                                        onClick={() => {
                                            setIsAPIKeyInputDisabled(false);
                                            setValue(
                                                "paywall_integration.api_key",
                                                null,
                                            );
                                        }}
                                        iconButton
                                        variant={ButtonVariant.BLACK_OUTLINED}
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </StandartButton>
                                </Box>
                            </Tooltip>
                        )}
                    </Stack>
                    <TextField
                        label={"Product Price ID"}
                        {...register("paywall_integration.product_price_id")}
                        style={{ width: "100%" }}
                        className="mt-3 text-field-ui-workaround"
                    />
                </>
            )}
        </>
    );
};
